import { useTranslation } from 'react-i18next';
import type { CropType } from '../../services/cropType.service';
import type { Region } from '../../services/region.service';
import type { SeasonStatus } from '../../utils/seasonStatus';
import type { CropStatusFilters, ViewMode } from './types';
import { UPCOMING_THRESHOLD_OPTIONS } from './types';
import { SegmentedControl, TableIcon, TimelineIcon } from '../../components/core';

interface Props {
  filters: CropStatusFilters;
  cropTypes: CropType[];
  regions: Region[];
  disabled: boolean;
  onChange: (_patch: Partial<CropStatusFilters>) => void;
  onClear: () => void;
}

export function FilterBar({ filters, cropTypes, regions, disabled, onChange, onClear }: Props) {
  const handleViewToggle = (mode: string) => {
    onChange({ viewMode: mode as ViewMode });
  };
  const { t } = useTranslation();
  const hasAnyFilter = !!(filters.cropTypeId || filters.regionId);

  return (
    <div className="mb-5 flex flex-col gap-4">
      {/* View toggle — full width on mobile with rounded edges, inline on desktop */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="sm:hidden font-body text-xs font-bold uppercase tracking-wide text-gray-500">
          {t('common.filters') || 'Filters:'}
        </span>
        <div className="w-full sm:w-auto">
          <SegmentedControl
            value={filters.viewMode ?? 'table'}
            options={[
              { label: t('cropStatus.viewMode.list'), value: 'table', icon: <TableIcon /> },
              { label: t('cropStatus.viewMode.timeline'), value: 'timeline', icon: <TimelineIcon /> },
            ]}
            onChange={handleViewToggle}
          />
        </div>
      </div>

      {/* Filters grid — 2x2 on mobile, flex row on desktop */}
      <div className="grid gap-2 grid-cols-2 sm:flex sm:flex-row sm:flex-wrap sm:items-end sm:gap-3">
        {/* Status — required in table mode, optional in timeline */}
        <div className="flex flex-col gap-1">
          <label htmlFor="status-filter" className="hidden sm:block font-body text-xs font-bold uppercase tracking-wide text-green-800">
            {t('cropStatus.statusLabel')}{' '}
            {filters.viewMode !== 'timeline' && (
              <span className="text-red-500">{t('cropStatus.statusRequired')}</span>
            )}
          </label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={e => onChange({ status: e.target.value as SeasonStatus | '' })}
            disabled={disabled}
            className="appearance-none rounded-md border-2 border-green-600 bg-green-50 px-3.5 py-[11px] font-body text-sm font-semibold text-green-900 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
          >
            {filters.viewMode === 'timeline' && (
              <option value="">{t('cropStatus.statusAllStatuses')}</option>
            )}
            <option value="in-season">{t('cropStatus.statusInSeason')}</option>
            <option value="upcoming">{t('cropStatus.statusUpcoming')}</option>
          </select>
        </div>

        {/* Crop Type — optional */}
        <div className="flex flex-col gap-1">
          <label htmlFor="crop-type-filter" className="hidden sm:block font-body text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('cropStatus.cropTypeLabel')}{' '}
            <span className="text-xs font-normal normal-case text-gray-400">{t('cropStatus.cropTypeOptional')}</span>
          </label>
          <select
            id="crop-type-filter"
            value={filters.cropTypeId}
            onChange={e => onChange({ cropTypeId: e.target.value })}
            disabled={disabled}
            className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3.5 py-[11px] font-body text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
          >
            <option value="">{t('cropStatus.cropTypePlaceholder')}</option>
            {cropTypes.map(ct => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
          </select>
        </div>

        {/* Region — optional */}
        <div className="flex flex-col gap-1">
          <label htmlFor="region-filter" className="hidden sm:block font-body text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('cropStatus.regionLabel')}{' '}
            <span className="text-xs font-normal normal-case text-gray-400">{t('cropStatus.regionOptional')}</span>
          </label>
          <select
            id="region-filter"
            value={filters.regionId}
            onChange={e => onChange({ regionId: e.target.value })}
            disabled={disabled}
            className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3.5 py-[11px] font-body text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
          >
            <option value="">{t('cropStatus.regionPlaceholder')}</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        {/* Upcoming Window — always visible on mobile in grid, conditionally on desktop */}
        <div className="flex flex-col gap-1">
          <label htmlFor="upcoming-window-filter" className="hidden sm:block font-body text-xs font-bold uppercase tracking-wide text-green-800">
            {t('cropStatus.upcomingWindowLabel')}{' '}
            <span className="text-xs font-normal normal-case text-green-600">{t('cropStatus.upcomingWindowSubLabel')}</span>
          </label>
          <select
            id="upcoming-window-filter"
            value={filters.upcomingThresholdDays}
            onChange={e => onChange({ upcomingThresholdDays: Number(e.target.value) })}
            disabled={disabled || filters.status === 'in-season'}
            className="appearance-none rounded-md border-2 border-green-600 bg-green-50 px-3.5 py-[11px] font-body text-sm font-semibold text-green-900 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
          >
            {UPCOMING_THRESHOLD_OPTIONS.map(days => (
              <option key={days} value={days}>{t('cropStatus.upcomingWindowOption', { days })}</option>
            ))}
          </select>
        </div>

        {/* Clear button — spans full width below grid on mobile, inline on desktop */}
        {hasAnyFilter && (
          <button
            type="button"
            onClick={onClear}
            className="col-span-2 font-body text-sm text-gray-400 hover:text-gray-600 sm:col-span-1 sm:self-end"
          >
            <span aria-hidden="true">✕</span> {t('cropStatus.clearFilters')}
          </button>
        )}
      </div>
    </div>
  );
}
